import { useTranslation } from 'react-i18next';
import type { ShippingAddressDraft } from '../shippingAddressTypes.js';

export interface ShippingAddressFieldsProps {
  readonly value: ShippingAddressDraft;
  readonly onChange: (next: ShippingAddressDraft) => void;
  /** Amazon-style save — unchecked by default; wire only when callback provided. */
  readonly saveAddressToMembership?: boolean;
  readonly onSaveAddressToMembershipChange?: (next: boolean) => void;
  /** When true, omit fieldset legend (settings section supplies its own title). */
  readonly hideLegend?: boolean;
}

export function ShippingAddressFields({
  value,
  onChange,
  saveAddressToMembership = false,
  onSaveAddressToMembershipChange,
  hideLegend = false,
}: ShippingAddressFieldsProps): JSX.Element {
  const { t } = useTranslation('customer');

  const patch = (partial: Partial<ShippingAddressDraft>): void => {
    onChange({ ...value, ...partial });
  };

  return (
    <fieldset
      className="m-0 flex flex-col gap-2 border-0 p-0"
      data-testid="shipping-address-fields"
    >
      {hideLegend ? null : (
        <legend className="mb-1 text-sm font-semibold">
          {t('checkout.shipping.addressLegend')}
        </legend>
      )}
      <AddressInput
        label={t('checkout.shipping.recipientName')}
        value={value.recipientName}
        onChange={(v) => patch({ recipientName: v })}
        testId="shipping-address-recipient"
        autoComplete="name"
      />
      <AddressInput
        label={t('checkout.shipping.line1')}
        value={value.line1}
        onChange={(v) => patch({ line1: v })}
        testId="shipping-address-line1"
        autoComplete="address-line1"
      />
      <AddressInput
        label={t('checkout.shipping.line2')}
        value={value.line2}
        onChange={(v) => patch({ line2: v })}
        testId="shipping-address-line2"
        autoComplete="address-line2"
        optional
      />
      <div className="grid grid-cols-2 gap-2">
        <AddressInput
          label={t('checkout.shipping.postalCode')}
          value={value.postalCode}
          onChange={(v) => patch({ postalCode: v })}
          testId="shipping-address-postal"
          autoComplete="postal-code"
        />
        <AddressInput
          label={t('checkout.shipping.city')}
          value={value.city}
          onChange={(v) => patch({ city: v })}
          testId="shipping-address-city"
          autoComplete="address-level2"
        />
      </div>
      <AddressInput
        label={t('checkout.shipping.countryCode')}
        value={value.countryCode}
        onChange={(v) => patch({ countryCode: v.toUpperCase().slice(0, 2) })}
        testId="shipping-address-country"
        autoComplete="country"
        maxLength={2}
      />
      <AddressInput
        label={t('checkout.shipping.phone')}
        value={value.phone}
        onChange={(v) => patch({ phone: v })}
        testId="shipping-address-phone"
        autoComplete="tel"
        optional
      />
      <AddressInput
        label={t('checkout.shipping.email')}
        value={value.email}
        onChange={(v) => patch({ email: v })}
        testId="shipping-address-email"
        autoComplete="email"
        inputType="email"
        optional
      />
      {onSaveAddressToMembershipChange != null ? (
        <label className="flex items-start gap-2 text-sm" data-testid="shipping-save-address">
          <input
            type="checkbox"
            className="mt-1"
            checked={saveAddressToMembership === true}
            onChange={(e) => onSaveAddressToMembershipChange(e.target.checked)}
            data-testid="shipping-save-address-checkbox"
          />
          <span>{t('checkout.shipping.saveAddressForNextTime')}</span>
        </label>
      ) : null}
    </fieldset>
  );
}

function AddressInput({
  label,
  value,
  onChange,
  testId,
  autoComplete,
  optional = false,
  maxLength,
  inputType = 'text',
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly testId: string;
  readonly autoComplete?: string;
  readonly optional?: boolean;
  readonly maxLength?: number;
  readonly inputType?: 'text' | 'email';
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span>
        {label}
        {optional ? '' : ' *'}
      </span>
      <input
        type={inputType}
        className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required={!optional}
      />
    </label>
  );
}
